import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { MdSearch } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { DataTablePlaceholder } from '../../../components/table/data-table-placeholder/data-table-placeholder';

type SelectableOption = {
  id: string;
  name: string;
};

export const AdvancedSearchTable = <T extends SelectableOption>({
  title,
  items,
  selectedItem,
  setSelected,
  onAddItem,
  renderAdditionalColumnHeaders,
  renderAdditionalColumns,
}: {
  title: string;
  items: T[];
  selectedItem: T | undefined;
  setSelected: (item: T) => void;
  onAddItem: (item: T) => void;
  renderAdditionalColumnHeaders?: () => React.ReactNode;
  renderAdditionalColumns?: (item: T) => React.ReactNode;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <DataTablePlaceholder
        icon={<MdSearch size={24} />}
        title={t('noResultsFound')}
        description={t('noResultsFoundDescription')}
      />
    );
  }

  return (
    <TableContainer component={Paper} sx={{ height: '100%' }}>
      <Table>
        <TableHead
          sx={{
            bgcolor: grey[50],
            border: 0.5,
            borderRadius: 2,
            borderWidth: 1,
            borderColor: greyscaleLight[200],
          }}
        >
          <TableRow>
            <TableCell>{title}</TableCell>
            {renderAdditionalColumnHeaders?.()}
          </TableRow>
        </TableHead>
        <TableBody sx={{ overflowY: 'scroll' }}>
          {items.map((item) => {
            const { id, name } = item;
            const isSelected = selectedItem?.id === item.id;
            return (
              <TableRow
                key={id}
                onClick={() => setSelected(item)}
                onDoubleClick={() => onAddItem(item)}
                sx={{
                  ':hover': {
                    cursor: 'pointer',
                  },
                  backgroundColor: isSelected ? '#F5F8FF' : 'transparent',
                }}
              >
                <TableCell component="th" scope="row">
                  {name}
                </TableCell>
                {renderAdditionalColumns?.(item)}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
